import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Cpu,
  LogOut,
  Phone,
  Smartphone,
  Trash2,
  RefreshCw,
  Inbox,
  Loader2,
  CheckCircle2,
  Clock,
  Sparkles,
  Search,
  Star,
  XCircle,
  Eye,
  EyeOff,
  Mail,
  Newspaper,
} from "lucide-react";
import { api, clearToken, getToken } from "../lib/api";
import { AdminBlog } from "../components/AdminBlog";

const leadStatusOptions = [
  { value: "new", label: "Νέο" },
  { value: "in_progress", label: "Σε εξέλιξη" },
  { value: "done", label: "Ολοκληρώθηκε" },
];

const leadStatusBadgeClass = {
  new: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  in_progress: "bg-amber-500/15 border-amber-500/40 text-amber-300",
  done: "bg-slate-500/15 border-slate-500/40 text-slate-300",
};

const reviewStatusOptions = [
  { value: "pending", label: "Σε αναμονή" },
  { value: "approved", label: "Εγκεκριμένη" },
  { value: "rejected", label: "Απορρίφθηκε" },
];

const reviewStatusBadgeClass = {
  pending: "bg-amber-500/15 border-amber-500/40 text-amber-300",
  approved: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  rejected: "bg-red-500/15 border-red-500/40 text-red-300",
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");

  // Leads state
  const [leads, setLeads] = useState([]);
  const [leadStats, setLeadStats] = useState({ total: 0, new: 0, in_progress: 0, done: 0 });
  const [leadFilter, setLeadFilter] = useState("all");
  const [leadSearch, setLeadSearch] = useState("");

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [reviewFilter, setReviewFilter] = useState("all");
  const [reviewSearch, setReviewSearch] = useState("");

  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, leadStatsRes, reviewsRes, reviewStatsRes] = await Promise.all([
        api.get("/admin/leads"),
        api.get("/admin/stats"),
        api.get("/admin/reviews"),
        api.get("/admin/reviews/stats"),
      ]);
      setLeads(leadsRes.data);
      setLeadStats(leadStatsRes.data);
      setReviews(reviewsRes.data);
      setReviewStats(reviewStatsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  // Lead actions
  const updateLeadStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status: newStatus });
      load();
    } catch (err) {
      console.error(err);
    }
  };
  const removeLead = async (id) => {
    if (!window.confirm("Διαγραφή αιτήματος;")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      load();
    } catch (err) {
      console.error(err);
    }
  };

  // Review actions
  const updateReviewStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { status: newStatus });
      load();
    } catch (err) {
      console.error(err);
    }
  };
  const removeReview = async (id) => {
    if (!window.confirm("Διαγραφή κριτικής;")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      load();
    } catch (err) {
      console.error(err);
    }
  };

  const filteredLeads = leads.filter((l) => {
    if (leadFilter !== "all" && l.status !== leadFilter) return false;
    if (leadSearch) {
      const s = leadSearch.toLowerCase();
      return (
        l.name.toLowerCase().includes(s) ||
        l.phone.toLowerCase().includes(s) ||
        (l.device || "").toLowerCase().includes(s) ||
        (l.message || "").toLowerCase().includes(s)
      );
    }
    return true;
  });

  const filteredReviews = reviews.filter((r) => {
    if (reviewFilter !== "all" && r.status !== reviewFilter) return false;
    if (reviewSearch) {
      const s = reviewSearch.toLowerCase();
      return (
        r.name.toLowerCase().includes(s) ||
        r.comment.toLowerCase().includes(s) ||
        (r.service || "").toLowerCase().includes(s)
      );
    }
    return true;
  });

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("el-GR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen">
      <header className="border-b border-emerald-900/40 bg-[#060f1d]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
              <Cpu className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <div className="text-white font-bold leading-tight">Repairzone Admin</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-400/80">Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="btn-outline rounded-md px-3 py-2 text-sm inline-flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Ανανέωση
            </button>
            <button
              onClick={logout}
              className="rounded-md px-3 py-2 text-sm inline-flex items-center gap-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Έξοδος
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-emerald-900/40">
          <TabButton active={tab === "leads"} onClick={() => setTab("leads")} count={leadStats.new}>
            <Mail className="w-4 h-4" /> Αιτήματα
          </TabButton>
          <TabButton active={tab === "reviews"} onClick={() => setTab("reviews")} count={reviewStats.pending}>
            <Star className="w-4 h-4" /> Κριτικές
          </TabButton>
          <TabButton active={tab === "blog"} onClick={() => setTab("blog")} data-testid="admin-tab-blog">
            <Newspaper className="w-4 h-4" /> Blog
          </TabButton>
        </div>

        {tab === "blog" && <AdminBlog />}

        {tab === "leads" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatCard icon={Inbox} label="Σύνολο" value={leadStats.total} color="emerald" />
              <StatCard icon={Sparkles} label="Νέα" value={leadStats.new} color="emerald" />
              <StatCard icon={Loader2} label="Σε εξέλιξη" value={leadStats.in_progress} color="amber" />
              <StatCard icon={CheckCircle2} label="Ολοκληρωμένα" value={leadStats.done} color="slate" />
            </div>

            <div className="pcb-card p-4 mb-6 flex flex-col md:flex-row gap-3 md:items-center">
              <div className="flex flex-wrap gap-2">
                <FilterButton active={leadFilter === "all"} onClick={() => setLeadFilter("all")}>Όλα</FilterButton>
                {leadStatusOptions.map((s) => (
                  <FilterButton key={s.value} active={leadFilter === s.value} onClick={() => setLeadFilter(s.value)}>
                    {s.label}
                  </FilterButton>
                ))}
              </div>
              <div className="md:ml-auto relative md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-300/60" />
                <input
                  type="text"
                  value={leadSearch}
                  onChange={(e) => setLeadSearch(e.target.value)}
                  placeholder="Αναζήτηση..."
                  className="w-full rounded-md bg-[#04101c] border border-emerald-900/70 pl-10 pr-3 py-2 text-sm text-white placeholder:text-emerald-100/30 focus:border-emerald-500 transition-colors outline-none"
                />
              </div>
            </div>

            {loading ? (
              <Loading />
            ) : filteredLeads.length === 0 ? (
              <EmptyState icon={Inbox} title="Δεν υπάρχουν αιτήματα" subtitle="Όταν κάποιος συμπληρώσει τη φόρμα, θα εμφανιστεί εδώ." />
            ) : (
              <div className="space-y-3">
                {filteredLeads.map((lead) => (
                  <div key={lead.id} className="pcb-card p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[260px]">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="text-white font-bold text-lg">{lead.name}</div>
                          <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded border ${leadStatusBadgeClass[lead.status]}`}>
                            {leadStatusOptions.find((s) => s.value === lead.status)?.label || lead.status}
                          </span>
                          <span className="text-xs text-emerald-100/50 inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {formatDate(lead.created_at)}
                          </span>
                        </div>
                        <div className="mt-3 grid sm:grid-cols-2 gap-3">
                          <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-sm text-emerald-300 hover:text-emerald-200">
                            <Phone className="w-4 h-4" /> {lead.phone}
                          </a>
                          {lead.device && (
                            <div className="flex items-center gap-2 text-sm text-emerald-50/80">
                              <Smartphone className="w-4 h-4 text-emerald-400/70" /> {lead.device}
                            </div>
                          )}
                        </div>
                        {lead.message && (
                          <div className="mt-3 p-3 rounded-md bg-[#04101c] border border-emerald-900/60 text-sm text-emerald-50/85 whitespace-pre-wrap">
                            {lead.message}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 min-w-[180px]">
                        <select
                          value={lead.status}
                          onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                          className="rounded-md bg-[#04101c] border border-emerald-900/70 px-3 py-2 text-sm text-white focus:border-emerald-500 outline-none"
                        >
                          {leadStatusOptions.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                        <div className="flex gap-2">
                          <a
                            href={`https://wa.me/${lead.phone.replace(/[^0-9]/g, "")}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex-1 text-center rounded-md px-3 py-2 text-xs border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors"
                          >
                            WhatsApp
                          </a>
                          <button
                            onClick={() => removeLead(lead.id)}
                            aria-label="delete"
                            className="rounded-md px-3 py-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "reviews" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatCard icon={Star} label="Σύνολο" value={reviewStats.total} color="emerald" />
              <StatCard icon={Sparkles} label="Σε αναμονή" value={reviewStats.pending} color="amber" />
              <StatCard icon={Eye} label="Εγκεκριμένες" value={reviewStats.approved} color="emerald" />
              <StatCard icon={EyeOff} label="Απορριφθείσες" value={reviewStats.rejected} color="red" />
            </div>

            <div className="pcb-card p-4 mb-6 flex flex-col md:flex-row gap-3 md:items-center">
              <div className="flex flex-wrap gap-2">
                <FilterButton active={reviewFilter === "all"} onClick={() => setReviewFilter("all")}>Όλες</FilterButton>
                {reviewStatusOptions.map((s) => (
                  <FilterButton key={s.value} active={reviewFilter === s.value} onClick={() => setReviewFilter(s.value)}>
                    {s.label}
                  </FilterButton>
                ))}
              </div>
              <div className="md:ml-auto relative md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-300/60" />
                <input
                  type="text"
                  value={reviewSearch}
                  onChange={(e) => setReviewSearch(e.target.value)}
                  placeholder="Αναζήτηση..."
                  className="w-full rounded-md bg-[#04101c] border border-emerald-900/70 pl-10 pr-3 py-2 text-sm text-white placeholder:text-emerald-100/30 focus:border-emerald-500 transition-colors outline-none"
                />
              </div>
            </div>

            {loading ? (
              <Loading />
            ) : filteredReviews.length === 0 ? (
              <EmptyState icon={Star} title="Δεν υπάρχουν κριτικές" subtitle="Όταν πελάτες αφήνουν αξιολόγηση, θα εμφανιστεί εδώ." />
            ) : (
              <div className="space-y-3">
                {filteredReviews.map((review) => (
                  <div key={review.id} className="pcb-card p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[260px]">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="text-white font-bold text-lg">{review.name}</div>
                          <div className="flex gap-0.5">
                            {Array.from({ length: review.rating }).map((_, k) => (
                              <Star key={k} className="w-4 h-4 text-amber-400 fill-amber-400" />
                            ))}
                          </div>
                          <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded border ${reviewStatusBadgeClass[review.status]}`}>
                            {reviewStatusOptions.find((s) => s.value === review.status)?.label || review.status}
                          </span>
                          <span className="text-xs text-emerald-100/50 inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {formatDate(review.created_at)}
                          </span>
                        </div>
                        {review.service && (
                          <div className="mt-2 text-xs text-emerald-300/80">{review.service}</div>
                        )}
                        <div className="mt-3 p-3 rounded-md bg-[#04101c] border border-emerald-900/60 text-sm text-emerald-50/85 whitespace-pre-wrap">
                          {review.comment}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 min-w-[180px]">
                        {review.status !== "approved" && (
                          <button
                            onClick={() => updateReviewStatus(review.id, "approved")}
                            className="rounded-md px-3 py-2 text-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-colors inline-flex items-center justify-center gap-2"
                          >
                            <CheckCircle2 className="w-4 h-4" /> Έγκριση
                          </button>
                        )}
                        {review.status !== "rejected" && (
                          <button
                            onClick={() => updateReviewStatus(review.id, "rejected")}
                            className="rounded-md px-3 py-2 text-sm border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors inline-flex items-center justify-center gap-2"
                          >
                            <XCircle className="w-4 h-4" /> Απόρριψη
                          </button>
                        )}
                        <button
                          onClick={() => removeReview(review.id)}
                          className="rounded-md px-3 py-2 text-sm border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors inline-flex items-center justify-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" /> Διαγραφή
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

const TabButton = ({ active, onClick, count, children, ...rest }) => (
  <button
    onClick={onClick}
    {...rest}
    className={`relative px-4 py-3 text-sm inline-flex items-center gap-2 border-b-2 transition-colors ${
      active
        ? "border-emerald-500 text-emerald-300"
        : "border-transparent text-emerald-100/60 hover:text-emerald-200"
    }`}
  >
    {children}
    {count > 0 && (
      <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-emerald-950 text-[11px] font-bold">
        {count}
      </span>
    )}
  </button>
);

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorMap = {
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    slate: "text-slate-300 bg-slate-500/10 border-slate-500/30",
    red: "text-red-400 bg-red-500/10 border-red-500/30",
  };
  return (
    <div className="pcb-card p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-md border flex items-center justify-center ${colorMap[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-2xl font-extrabold text-white">{value}</div>
        <div className="text-xs text-emerald-100/60">{label}</div>
      </div>
    </div>
  );
};

const FilterButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
      active
        ? "bg-emerald-500/20 border-emerald-500 text-emerald-200"
        : "border-emerald-900/60 text-emerald-100/70 hover:border-emerald-500/40"
    }`}
  >
    {children}
  </button>
);

const Loading = () => (
  <div className="text-center py-16 text-emerald-100/60">
    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-emerald-400" />
    Φόρτωση...
  </div>
);

const EmptyState = ({ icon: Icon, title, subtitle }) => (
  <div className="pcb-card p-12 text-center">
    <Icon className="w-10 h-10 mx-auto text-emerald-500/50" />
    <div className="mt-3 text-white font-semibold">{title}</div>
    <div className="text-sm text-emerald-100/60 mt-1">{subtitle}</div>
  </div>
);

export default AdminDashboard;
